import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [branding, setBranding] = useState({ company_name: "itotal4u", tagline: "", logo_url: null });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/settings/branding/public");
                setBranding(data || { company_name: "itotal4u" });
            } catch { /* fallback al default */ }
        })();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        try {
            const u = await login(email, password);
            // Si la cuenta fue reseteada con contraseña temporal, forzar cambio inmediato
            if (u && u.must_change_password) {
                navigate("/change-password", { replace: true, state: { forced: true } });
                return;
            }
            const to = location.state?.from || "/dashboard";
            navigate(to, { replace: true, state: { user: u } });
        } catch (err) {
            setError(formatApiError(err, "No pudimos iniciar sesión"));
        } finally { setLoading(false); }
    };

    const googleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const logoSrc = branding.logo_url ? `${REACT_APP_BACKEND_URL}${branding.logo_url}` : null;

    return (
        <div className="auth-shell" data-testid="login-page">
            <section className="auth-hero">
                <div className="brand auth-brand">
                    {logoSrc ? (
                        <img src={logoSrc} alt={branding.company_name} className="auth-brand-logo" data-testid="login-logo" />
                    ) : (
                        <>itotal<span className="accent">4u</span></>
                    )}
                </div>
                <div>
                    <h2>Soporte <em>sin fricción</em> para tu equipo y tus clientes.</h2>
                    <p className="tag">Helpdesk moderno con bandeja colaborativa, SLA claros, auditoría completa y plantillas para responder más rápido. Todo en un solo lugar.</p>
                </div>
                <div className="meta">v0.1.0 · Auth · Empresas · Personas · Tickets · SLA</div>
            </section>

            <section className="auth-pane">
                <div className="auth-card">
                    {logoSrc && <img src={logoSrc} alt={branding.company_name} className="auth-pane-logo" data-testid="login-logo-pane" />}
                    <h3>Iniciar sesión</h3>
                    <p className="sub">Accede con tu cuenta de {branding.company_name || "itotal4u"}.</p>

                    {error && <div className="alert alert-error" data-testid="login-error">{error}</div>}

                    <form onSubmit={submit} className="row" style={{ gap: 14 }}>
                        <div className="field">
                            <label htmlFor="email">Correo electrónico</label>
                            <input id="email" type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@empresa.com" required autoComplete="email" data-testid="login-email" />
                        </div>
                        <div className="field">
                            <label htmlFor="password">Contraseña</label>
                            <input id="password" type="password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required autoComplete="current-password" data-testid="login-password" />
                        </div>
                        <div className="flex justify-between items-center" style={{ marginTop: 4 }}>
                            <Link to="/forgot-password" className="linklike" data-testid="login-forgot">¿Olvidaste tu contraseña?</Link>
                        </div>
                        <button className="btn btn-primary btn-block" type="submit" disabled={loading} data-testid="login-submit">
                            {loading ? <span className="loader" /> : "Entrar"}
                        </button>
                    </form>

                    <div className="divider">o continúa con</div>
                    <button className="btn btn-google btn-block" onClick={googleLogin} data-testid="login-google">
                        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 34.933 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.094 5.569.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                        </svg>
                        Continuar con Google
                    </button>

                    <div className="switch">
                        ¿Nuevo aquí? <Link to="/register" data-testid="login-to-register">Crear cuenta</Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
